import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const authHeader = (): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Sub-tarefas do serviço de uma esteira (at-89, ADR 0041).
 *
 * O ADMIN/TI cadastra o catálogo por esteira, o Chefe escolhe quais valem para o veículo (junto do
 * planejamento) e o colaborador marca o que terminou. A leitura do progresso é o que responde,
 * em 7 telas, a pergunta do associado: "o que já foi feito no meu carro?".
 *
 * ⚠️ Marcar NÃO conclui o serviço da esteira, e concluir o serviço não descarta o que ficou sem
 * marcar — as pendentes seguem visíveis. Quem declara conclusão continua sendo o colaborador ao
 * finalizar a atividade e o Chefe ao mover o veículo (ADR 0021).
 */
export interface SubtarefaDefinicao {
  id: string;
  esteiraId: string;
  nome: string;
  ordem: number;
  ativa: boolean;
  esteira?: { id: string; nome: string; cor?: string };
}

export interface SubtarefaDoVeiculo {
  /**
   * at-90: id da INSTÂNCIA desta sub-tarefa neste atendimento — a âncora do comentário de
   * sub-tarefa. Diferente do `definicaoId`, que é do catálogo e é o mesmo em todo veículo.
   */
  atividadeSubtarefaId: string;
  definicaoId: string;
  nome: string | null;
  ordem: number;
  concluida: boolean;
  concluidoPorNome: string | null;
  concluidoEm: string | null;
}

export interface EsteiraComSubtarefas {
  esteiraId: string;
  esteiraNome: string | null;
  esteiraCor: string | null;
  estadoServico: string;
  /** false = a esteira saiu do planejamento, mas o que já foi executado continua registrado. */
  planejada: boolean;
  atual: boolean;
  subtarefas: SubtarefaDoVeiculo[];
  totalConcluidas: number;
  total: number;
}

export interface ProgressoSubtarefas {
  carId: string;
  esteiras: EsteiraComSubtarefas[];
  totalConcluidas: number;
  total: number;
}

/** Catálogo de uma esteira (ou de todas). ADMIN/TI e Chefe (leitura). */
export const listarSubtarefas = async (
  esteiraId?: string,
  includeInactive = false,
): Promise<SubtarefaDefinicao[]> => {
  const params = new URLSearchParams();
  if (esteiraId) params.set('esteiraId', esteiraId);
  if (includeInactive) params.set('includeInactive', 'true');

  const query = params.toString();
  const { data } = await axios.get(`${BACKEND_URL}/subtarefas${query ? `?${query}` : ''}`, {
    headers: authHeader(),
  });
  return data;
};

export const criarSubtarefa = async (payload: {
  esteiraId: string;
  nome: string;
}): Promise<SubtarefaDefinicao> => {
  const { data } = await axios.post(`${BACKEND_URL}/subtarefas`, payload, { headers: authHeader() });
  return data;
};

export const atualizarSubtarefa = async (
  id: string,
  payload: { nome?: string; ativa?: boolean },
): Promise<SubtarefaDefinicao> => {
  const { data } = await axios.patch(`${BACKEND_URL}/subtarefas/${id}`, payload, {
    headers: authHeader(),
  });
  return data;
};

/** Soft-delete: some dos planejamentos novos, o que já foi executado continua registrado. */
export const desativarSubtarefa = async (id: string): Promise<SubtarefaDefinicao> => {
  const { data } = await axios.delete(`${BACKEND_URL}/subtarefas/${id}`, { headers: authHeader() });
  return data;
};

/**
 * ⚠️ Lista DECLARATIVA E COMPLETA: precisa trazer todas as sub-tarefas ATIVAS da esteira, na ordem
 * final. Lista parcial é recusada pelo backend (ordem ambígua não se adivinha).
 */
export const reordenarSubtarefas = async (
  esteiraId: string,
  ids: string[],
): Promise<SubtarefaDefinicao[]> => {
  const { data } = await axios.put(
    `${BACKEND_URL}/subtarefas/reorder`,
    { esteiraId, ids },
    { headers: authHeader() },
  );
  return data;
};

/** Progresso do veículo — o que já foi feito, por esteira. */
export const obterProgressoDoVeiculo = async (carId: string): Promise<ProgressoSubtarefas> => {
  const { data } = await axios.get(`${BACKEND_URL}/subtarefas/cars/${carId}`, {
    headers: authHeader(),
  });
  return data;
};

/**
 * Marca ou reabre uma sub-tarefa. Exige a senha do próprio colaborador — o registro diz que uma
 * pessoa específica declarou ter feito aquele serviço, e é isso que a Recepção repassa ao associado.
 */
export const marcarSubtarefa = async (
  carId: string,
  definicaoId: string,
  concluida: boolean,
  password: string,
): Promise<ProgressoSubtarefas> => {
  const { data } = await axios.patch(
    `${BACKEND_URL}/subtarefas/cars/${carId}/${definicaoId}`,
    { concluida, password },
    { headers: authHeader() },
  );
  return data;
};
